import { createContext, Dispatch, Context } from 'react'

import { IReducerContextProps, ReducerAction } from 'utility-tools/module/reducers'
import { TOverrideType } from 'utility-tools/module/types'
import { TUserData } from 'nexchange-service/module/auth/models'

enum CreateUserReducerAction {
    SET_FLOW_STATUS = 'SET_FLOW_STATUS',
    SET_USER_DATA = 'SET_USER_DATA',
    SET_IS_LOADING = 'SET_IS_LOADING',
    RESET_STATE = 'RESET_STATE',
}

enum RegistrationProcessState {
    PENDING = 'PENDING',
    SUCCESS = 'SUCCESS',
    ERROR = 'ERROR',
}

type TCreateUserState = {
    isLoading: boolean
    flowStatus: RegistrationProcessState
    userData: TUserData
}

const initialCreateUserState: TCreateUserState = {
    isLoading: false,
    flowStatus: RegistrationProcessState.PENDING,
    userData: null,
}

class SetFlowStatusAction extends ReducerAction<RegistrationProcessState> {
    constructor(status: RegistrationProcessState) {
        super(CreateUserReducerAction.SET_FLOW_STATUS, status)
    }
}

class SetUserDataAction extends ReducerAction<TUserData> {
    constructor(userData: TUserData) {
        super(CreateUserReducerAction.SET_USER_DATA, userData)
    }
}

class SetIsLoadingAction extends ReducerAction<boolean> {
    constructor(isLoading: boolean) {
        super(CreateUserReducerAction.SET_IS_LOADING, isLoading)
    }
}

class ResetStateAction extends ReducerAction<void> {
    constructor() {
        super(CreateUserReducerAction.RESET_STATE)
    }
}

type TCreateUserReducerAction =
    | SetFlowStatusAction
    | SetUserDataAction
    | SetIsLoadingAction
    | ResetStateAction

const createNewUserReducer = (
    state: TCreateUserState = initialCreateUserState,
    action: TCreateUserReducerAction,
): TCreateUserState => {
    const { type, payload } = action

    switch (type) {
        case CreateUserReducerAction.SET_FLOW_STATUS: {
            return <TCreateUserState>{
                ...state,
                flowStatus: payload,
            }
        }
        case CreateUserReducerAction.SET_USER_DATA: {
            return <TCreateUserState>{
                ...state,
                userData: payload,
            }
        }
        case CreateUserReducerAction.SET_IS_LOADING: {
            return <TCreateUserState>{
                ...state,
                isLoading: payload,
            }
        }
        case CreateUserReducerAction.RESET_STATE: {
            return initialCreateUserState
        }
        default:
            return state
    }
}

type TCreateUserContext = Context<
    TOverrideType<
        IReducerContextProps,
        {
            state: TCreateUserState
            dispatchAction?: Dispatch<TCreateUserReducerAction>
        }
    >
>

const CreateUserContext = createContext({
    state: initialCreateUserState,
}) as TCreateUserContext

export {
    RegistrationProcessState,
    CreateUserContext,
    initialCreateUserState,
    createNewUserReducer,
    SetFlowStatusAction,
    SetUserDataAction,
    SetIsLoadingAction,
    ResetStateAction,
}
